/**
 * The orchestrator's schedules for one market — `GET /foundations/schedules?country_key=`
 * (bata-ui#166 W1-1/W1-2, `docs/adr/2006`).
 *
 * Moved out of `FeedStatusScreen` in package A (2026-09-30): Data and schedules was two functions
 * on one page (client rule 3), so the schedules are their own page, `/foundations/schedules`, a
 * sub-entry of Data in the rail, and the feeds keep `/foundations/feeds`.
 *
 * **One line per row (client rule 2).** Each fact the old cells stacked — a run's outcome, when it
 * finished and its partition; a tick's time and its status; a cron and its zone — is a column of
 * its own. A dash stands where a row has nothing to say, and what each dash means is stated once
 * under the table. A zone every schedule shares is hoisted into the constants band.
 *
 * NOTHING HERE IS ASSERTED GREEN. A stopped or unknown schedule never reads as running
 * (`scheduleNext`); a stale report is "state unknown since".
 *
 * **The designer's template E (brief part 2 §6 «Schedule», Package C6).** The sentence (how many
 * run, and when the next tick is), three figures (schedules · next · stopped) and a fourth the
 * report carries (newest run failed), the one table with the next tick emphasised, the market as a
 * constant, the read and the report's time as provenance.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { readSchedules, type ScheduleStatus } from '@/api/generated';
import { useApiQuery } from '@/api';
import { formatDateTime, formatInteger } from '@/lib/format';
import { EmptyState, Skeleton, StatusIndicator, TruncatedText } from '@/primitives';
import {
  DataTable, IdTag, ListTemplate, ProblemNotice, statusColumn, useLocationSearch, tableFilterHref,
  type ConstantPair, type DataColumn, type RowStatus, type TemplateKpi, type TemplateSentence,
} from '@/planning';

import { cycleKey, runOutcome, scheduleNext, tickStateKey, type ScheduleNext } from './data-schedules';
import { cronText, partitionText, scheduleTitle } from './schedule-words';
import { codeWords, ORCHESTRATOR_STATUS_KEY } from '@/planning/words';

/** Instants are read in UTC, the zone every schedule and batch time is recorded in. */
const ZONE = 'UTC';

export function when(value: string | null | undefined, language: string): string | null {
  if (value === null || value === undefined || Number.isNaN(Date.parse(value))) return null;
  return formatDateTime(value, language, ZONE);
}

/** The table's name in the address bar: `?schedules.f.<column>=v:<value>` is its filter (`DataTable.urlKey`, ADR 9069). */
const TABLE_KEY = 'schedules';
const SCHEDULES_PATH = '/foundations/schedules';

/**
 * The schedules table narrowed to what a card counts (ADR 9069): the market stays, the table's previous view goes.
 * `all` is the whole report; `stopped` is the status column's `stopped` (the statement `scheduleNext` makes, with the
 * same `now`); `failed` is the «newest run failed» column, which is `runOutcome(row).failed` — the card's own predicate.
 */
export function schedulesCardHref(search: string, card: 'all' | 'stopped' | 'failed'): string {
  switch (card) {
    case 'all': return tableFilterHref(SCHEDULES_PATH, search, TABLE_KEY, {});
    case 'stopped': return tableFilterHref(SCHEDULES_PATH, search, TABLE_KEY, { status: ['stopped'] });
    case 'failed': return tableFilterHref(SCHEDULES_PATH, search, TABLE_KEY, { last_run_failed: ['true'] });
  }
}

/** What the table's absences (written in words, ADR 9061) mean, for the rows that carry one. Pure, for the test. */
export interface ScheduleDashes {
  readonly noNextTick: boolean;
  readonly notACycle: boolean;
  readonly noRun: boolean;
}

export function scheduleDashes(rows: readonly ScheduleStatus[], now: number): ScheduleDashes {
  return {
    noNextTick: rows.some((row) => scheduleNext(row, now).kind !== 'scheduled'),
    notACycle: rows.some((row) => row.cycle === null),
    noRun: rows.some((row) => runOutcome(row).kind === 'none'),
  };
}

/** The state as a table status: ✓ running, — stopped, ● unknown since the report. */
function stateStatus(next: ScheduleNext, language: string): RowStatus {
  if (next.kind === 'scheduled') return { code: 'scheduled', mark: 'good', labelKey: 'foundations.schedules.state.running' };
  if (next.kind === 'stopped') return { code: 'stopped', mark: 'neutral', labelKey: 'foundations.schedules.state.stopped' };
  return {
    code: 'unknownSince', mark: 'open', labelKey: 'foundations.schedules.state.unknownSince',
    values: { when: when(next.reportedAt, language) ?? next.reportedAt },
  };
}

const STATE_FILTER_KEY: Readonly<Record<string, string>> = {
  scheduled: 'foundations.schedules.state.running',
  stopped: 'foundations.schedules.state.stopped',
  unknownSince: 'foundations.schedules.state.unknown',
};

/** The sentence over the report: how many run, and the next tick among them. Pure, for the test. */
export function schedulesSentence(rows: readonly ScheduleStatus[], now: number, format: (iso: string) => string): TemplateSentence {
  const next = rows.flatMap((row) => {
    const tick = scheduleNext(row, now);
    return tick.kind === 'scheduled' ? [tick.at] : [];
  }).sort((a, b) => Date.parse(a) - Date.parse(b))[0];
  const running = rows.filter((row) => scheduleNext(row, now).kind === 'scheduled').length;
  return next === undefined
    ? { key: 'foundations.schedules.sentence.noneRunning', values: { total: rows.length } }
    : { key: 'foundations.schedules.sentence.running', values: { running, when: format(next) } };
}

function OutcomeCell({ row }: { readonly row: ScheduleStatus }): React.JSX.Element {
  const { t } = useTranslation();
  const outcome = runOutcome(row);
  return (
    <td className="py-1">
      {outcome.kind === 'none' && t('foundations.schedules.word.noRun')}
      {outcome.kind === 'outcome' && (outcome.failed
        ? <StatusIndicator level="serious" labelKey={outcome.key} values={{ outcome: outcome.word }} />
        : t(outcome.key, { outcome: outcome.word }))}
      {outcome.kind === 'status' && (outcome.failed
        ? <StatusIndicator level="serious" labelKey="foundations.schedules.outcome.statusOnly" values={{ status: codeWords(t, ORCHESTRATOR_STATUS_KEY, outcome.status) }} />
        : t('foundations.schedules.outcome.statusOnly', { status: codeWords(t, ORCHESTRATOR_STATUS_KEY, outcome.status) }))}
    </td>
  );
}

/**
 * Words on one line with the «id» button beside them (client rule 2: a row is one line). The words
 * are cut with an ellipsis where the column is narrower, the whole text in a tooltip; the button
 * never shrinks and never wraps under them.
 */
function WordsWithId({ words, id, kind }: { readonly words: string; readonly id: string; readonly kind: string }): React.JSX.Element {
  return (
    <span className="flex flex-nowrap items-baseline gap-1">
      <TruncatedText text={words} className="min-w-0" />
      <span className="shrink-0"><IdTag id={id} kind={kind} /></span>
    </span>
  );
}

/** A schedule in words, its orchestrator name behind the «id» button (ADR 9057). */
function ScheduleName({ name }: { readonly name: string }): React.JSX.Element {
  const { t } = useTranslation();
  return <WordsWithId words={scheduleTitle(t, name)} id={name} kind={t('foundations.schedules.idKind.schedule')} />;
}

/** A cron in words; one this build cannot phrase is «Pianificazione personalizzata». The expression stays behind «id». */
function CronCell({ row }: { readonly row: ScheduleStatus }): React.JSX.Element {
  const { t, i18n } = useTranslation();
  if (row.cron === null || row.cron.trim() === '') return <td className="py-1">{t('foundations.schedules.noCron')}</td>;
  const words = cronText(t, i18n.language, row.cron, row.executionTimezone);
  return (
    <td className="py-1">
      <WordsWithId words={words ?? t('foundations.schedules.cron.custom')} id={row.cron} kind={t('foundations.schedules.idKind.cron')} />
    </td>
  );
}

/** A partition in words — «CO · 1 ottobre 2026» — its key behind «id»; a shape this build does not know, as sent. */
function PartitionCell({ row, language }: { readonly row: ScheduleStatus; readonly language: string }): React.JSX.Element {
  const { t } = useTranslation();
  const key = row.lastRun?.partitionKey ?? null;
  if (key === null) return <td className="py-1">{t('foundations.schedules.word.noRun')}</td>;
  const words = partitionText(key, language);
  if (words === null) return <td className="py-1 font-numeric" translate="no">{key}</td>;
  return (
    <td className="py-1">
      <WordsWithId words={words} id={key} kind={t('foundations.schedules.idKind.partition')} />
    </td>
  );
}

export function SchedulesTemplate({ country, rows, now, reportedAt }: {
  readonly country: string;
  readonly rows: readonly ScheduleStatus[];
  readonly now: number;
  readonly reportedAt: string | null;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const dashes = scheduleDashes(rows, now);
  const search = useLocationSearch();
  const columns = useMemo((): readonly DataColumn<ScheduleStatus>[] => [
    {
      id: 'schedule_name', headerKey: 'foundations.schedules.column.schedule', descriptionKey: 'foundations.help.schedulesPanel.colSchedule', kind: 'text',
      value: (row) => scheduleTitle(t, row.scheduleName),
      cell: (row) => <th scope="row" className="py-1 text-start font-normal"><ScheduleName name={row.scheduleName} /></th>,
    },
    {
      id: 'cycle', headerKey: 'foundations.schedules.column.cycle', descriptionKey: 'foundations.help.schedulesPanel.colCycle', kind: 'enum',
      value: (row) => row.cycle ?? 'none',
      label: (value) => t(cycleKey(value === 'none' ? null : value), { cycle: value }),
      cell: (row) => <td className="py-1">{t(cycleKey(row.cycle), { cycle: row.cycle ?? '' })}</td>,
    },
    {
      id: 'cron', headerKey: 'foundations.schedules.column.cron', descriptionKey: 'foundations.help.schedulesPanel.colCron', kind: 'text',
      value: (row) => (row.cron === null ? null : (cronText(t, locale, row.cron, row.executionTimezone) ?? t('foundations.schedules.cron.custom'))),
      cell: (row) => <CronCell row={row} />,
    },
    {
      id: 'execution_timezone', headerKey: 'foundations.schedules.column.zone', descriptionKey: 'foundations.help.schedulesPanel.colZone', kind: 'text', constantHoist: true, code: true,
      value: (row) => row.executionTimezone,
      cell: (row) => <td className="py-1 font-numeric" translate="no">{row.executionTimezone ?? t('absent.notStated')}</td>,
    },
    // The orchestrator's word is judged: a stale report is "state unknown since", never running.
    statusColumn<ScheduleStatus>({
      id: 'status', headerKey: 'foundations.schedules.column.status', descriptionKey: 'foundations.help.schedulesPanel.colStatus',
      status: (row) => stateStatus(scheduleNext(row, now), locale),
      label: (code) => t(STATE_FILTER_KEY[code] ?? 'foundations.schedules.state.unknown'),
    }),
    {
      id: 'next_tick_at', headerKey: 'foundations.schedules.column.nextTick', descriptionKey: 'foundations.help.schedulesPanel.colNextTick', kind: 'date', emphasis: true,
      value: (row) => (scheduleNext(row, now).kind === 'scheduled' ? row.nextTickAt : null),
      cell: (row) => {
        const next = scheduleNext(row, now);
        return <td className="py-1">{next.kind === 'scheduled' ? when(next.at, locale) : t('foundations.schedules.word.noNextTick')}</td>;
      },
    },
    {
      id: 'last_run_outcome', headerKey: 'foundations.schedules.column.lastRun', descriptionKey: 'foundations.help.schedulesPanel.colLastRun', kind: 'text',
      value: (row) => row.lastRun?.outcome ?? row.lastRun?.status ?? null,
      cell: (row) => <OutcomeCell row={row} />,
    },
    {
      // The statement the «Newest run failed» card counts, as a column of its own so the card has a filter that says exactly it.
      // A schedule with no reported run has no answer, not «no».
      id: 'last_run_failed', headerKey: 'foundations.schedules.column.runFailed', descriptionKey: 'foundations.help.schedulesPanel.colRunFailed', kind: 'enum',
      value: (row) => {
        const outcome = runOutcome(row);
        return outcome.kind === 'none' ? null : String(outcome.failed);
      },
      label: (value) => t(value === 'true' ? 'foundations.schedules.outcome.failed' : 'foundations.schedules.word.notFailed'),
      cell: (row) => {
        const outcome = runOutcome(row);
        return <td className="py-1">{outcome.kind === 'none' ? t('foundations.schedules.word.noRun') : t(outcome.failed ? 'foundations.schedules.outcome.failed' : 'foundations.schedules.word.notFailed')}</td>;
      },
    },
    {
      id: 'last_run_finished_at', headerKey: 'foundations.schedules.column.finished', descriptionKey: 'foundations.help.schedulesPanel.colFinished', kind: 'date',
      value: (row) => row.lastRun?.finishedAt ?? null,
      cell: (row) => <td className="py-1">{when(row.lastRun?.finishedAt, locale) ?? t('foundations.schedules.word.noRun')}</td>,
    },
    {
      id: 'last_run_partition', headerKey: 'foundations.schedules.column.partition', descriptionKey: 'foundations.help.schedulesPanel.colPartition', kind: 'text',
      value: (row) => {
        const key = row.lastRun?.partitionKey ?? null;
        return key === null ? null : (partitionText(key, locale) ?? key);
      },
      cell: (row) => <PartitionCell row={row} language={locale} />,
    },
    {
      id: 'last_tick_at', headerKey: 'foundations.schedules.column.lastTick', descriptionKey: 'foundations.help.schedulesPanel.colLastTick', kind: 'date',
      value: (row) => row.lastTickAt,
      cell: (row) => <td className="py-1">{when(row.lastTickAt, locale) ?? t('foundations.schedules.noTick')}</td>,
    },
    {
      id: 'last_tick_status', headerKey: 'foundations.schedules.column.tickStatus', descriptionKey: 'foundations.help.schedulesPanel.colTickStatus', kind: 'enum',
      value: (row) => row.lastTickStatus,
      // The orchestrator's tick status in words (audit A14); a status this build does not know, as sent.
      label: (value) => codeWords(t, ORCHESTRATOR_STATUS_KEY, value),
      cell: (row) => <td className="py-1">{row.lastTickStatus === null ? t('foundations.schedules.noTick') : codeWords(t, ORCHESTRATOR_STATUS_KEY, row.lastTickStatus)}</td>,
    },
    {
      id: 'cycle_tick_state', headerKey: 'foundations.schedules.column.cycleTick', descriptionKey: 'foundations.help.schedulesPanel.colCycleTick', kind: 'enum',
      value: (row) => row.cycleTickState ?? 'none',
      label: (value) => t(tickStateKey(value === 'none' ? null : value), { state: value }),
      cell: (row) => (
        <td className="py-1">
          {row.cycle === null ? t('foundations.schedules.word.notACycle') : t(tickStateKey(row.cycleTickState), { state: row.cycleTickState ?? '' })}
        </td>
      ),
    },
  ], [t, locale, now]);

  const nextTicks = rows.flatMap((row) => {
    const tick = scheduleNext(row, now);
    return tick.kind === 'scheduled' ? [{ at: tick.at, name: scheduleTitle(t, row.scheduleName) }] : [];
  }).sort((a, b) => Date.parse(a.at) - Date.parse(b.at));
  const first = nextTicks[0];
  const running = nextTicks.length;
  const stopped = rows.filter((row) => scheduleNext(row, now).kind === 'stopped').length;
  const failed = rows.filter((row) => {
    const outcome = runOutcome(row);
    return outcome.kind !== 'none' && outcome.failed;
  }).length;
  const kpis: readonly TemplateKpi[] = [
    {
      id: 'schedules', labelKey: 'foundations.schedules.kpi.schedules', descriptionKey: 'foundations.help.schedulesPanel.kpiSchedules', value: formatInteger(rows.length, locale), href: schedulesCardHref(search, 'all'),
      context: t('foundations.schedules.kpi.running', { running }),
    },
    {
      id: 'next', labelKey: 'foundations.schedules.kpi.next', descriptionKey: 'foundations.help.schedulesPanel.kpiNext',
      value: first === undefined ? null : when(first.at, locale),
      context: first === undefined ? t('foundations.schedules.kpi.noNext') : first.name,
    },
    { id: 'stopped', labelKey: 'foundations.schedules.kpi.stopped', descriptionKey: 'foundations.help.schedulesPanel.kpiStopped', value: formatInteger(stopped, locale), href: schedulesCardHref(search, 'stopped') },
    {
      id: 'failed', labelKey: 'foundations.schedules.kpi.failed', descriptionKey: 'foundations.help.schedulesPanel.kpiFailed', value: formatInteger(failed, locale), href: schedulesCardHref(search, 'failed'),
      ...(failed > 0 ? { alert: { key: 'foundations.schedules.outcome.failed' } } : {}),
    },
  ];
  const constants: readonly ConstantPair[] = [
    { id: 'market', labelKey: 'foundations.schedules.constant.market', value: country, mono: true },
  ];

  return (
    <ListTemplate
      sentence={schedulesSentence(rows, now, (iso) => when(iso, locale) ?? iso)}
      kpis={kpis}
      constants={constants}
      table={(
        <div className="flex flex-col gap-2">
          <DataTable sourceKey="foundations.help.schedulesPanel.source" helpValues={{ market: country, reportedAt: reportedAt === null ? t('absent.word.notStated') : (when(reportedAt, locale) ?? reportedAt) }}
            tourAnchor="foundations-schedules-table"
            urlKey={TABLE_KEY}
            density
            rows={rows}
            rowKey={(row) => row.scheduleName}
            captionKey="foundations.schedules.caption"
            exportId="foundations.schedules.table"
            exportTitleKey="foundations.schedules.title"
            rowProps={() => ({ className: 'border-hairline border-b whitespace-nowrap' })}
            columns={columns}
          />
          {(dashes.noNextTick || dashes.notACycle || dashes.noRun) && (
            <p className="text-caption text-ink-secondary flex flex-wrap gap-x-4">
              {dashes.noNextTick && <span>{t('foundations.schedules.dash.nextTick')}</span>}
              {dashes.notACycle && <span>{t('foundations.schedules.dash.notACycle')}</span>}
              {dashes.noRun && <span>{t('foundations.schedules.dash.noRun')}</span>}
            </p>
          )}
        </div>
      )}
      provenance={['foundations/schedules', ...(reportedAt === null ? [] : [when(reportedAt, locale) ?? reportedAt])]}
    />
  );
}

/** The market's schedules: the template, or the designed state that stands in for it. */
export function SchedulesPage({ country }: { readonly country: string }): React.JSX.Element {
  const schedules = useApiQuery(readSchedules, { query: { country_key: country } });
  // A report ages by minutes; the screen re-renders on every read that answers.
  const now = Date.now();
  if (schedules.isError) {
    return <ProblemNotice error={schedules.error} titleKey="foundations.schedules.loadFailed" onRetry={() => schedules.refetch()} />;
  }
  if (schedules.data === undefined) return <Skeleton rows={6} />;
  const reportedAt = schedules.data.reportedAt;
  if (schedules.data.schedules.length === 0) {
    return (
      <EmptyState
        titleKey={reportedAt === null ? 'foundations.schedules.neverTitle' : 'foundations.schedules.emptyTitle'}
        bodyKey={reportedAt === null ? 'foundations.schedules.neverBody' : 'foundations.schedules.emptyBody'}
        values={{ country }}
      />
    );
  }
  return <SchedulesTemplate country={country} rows={schedules.data.schedules} now={now} reportedAt={reportedAt} />;
}
